import { Autocomplete, AutocompleteItem } from '@heroui/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  buildTimezoneOptions,
  filterTimezoneOptions,
} from '../../../../../utils/timezones';
import { useSupplierForm } from '../../../SupplierFormProvider';
import { type SupplierFormData } from '../../../types';

/**
 * The distributor's time zone.
 *
 * Next to Language, because it is distributor identity in the same way the
 * language is, and that is where CS looks for it. Delivery days and cutoffs are
 * read in it, which makes Order Preferences the other candidate.
 *
 * No `Card` and no heading: this tab sits inside the form's card already, and
 * labels its rows rather than wrapping each one in a panel.
 */
export const TimezoneField: React.FC = () => {
  const { mode } = useSupplierForm();
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<SupplierFormData>();

  const timezone = watch('timezone');

  // Rebuilt only when the saved value changes, since it is the one zone that has
  // to be in the list even if this runtime does not consider it canonical.
  const options = useMemo(() => buildTimezoneOptions(timezone), [timezone]);

  /**
   * The text in the box, held here because the filtering is.
   *
   * HeroUI only filters a collection it owns, and this one is built from the
   * stored zone, so typing filtered nothing until the field took the job on. Once
   * the input is controlled, the states react-stately would have reset for us —
   * the revert on blur, the label after a pick — have to be set here too.
   */
  const [query, setQuery] = useState('');

  const selectedLabel =
    options.find((option) => option.value === timezone)?.label ?? '';

  // Follows the stored zone rather than the typing: the form loads, resets after a
  // save, and swaps distributor without this field being touched.
  useEffect(() => {
    setQuery(selectedLabel);
  }, [selectedLabel]);

  const visibleOptions = useMemo(
    () => filterTimezoneOptions(options, query),
    [options, query]
  );

  // Required on create only, matching the schema, so the asterisk does not promise
  // something validation will not enforce.
  const isCreate = mode === 'create';

  // Only an existing distributor can be without a zone, so only there does the
  // fallback need explaining. Everywhere else the line says what the field is for.
  const description =
    timezone || isCreate
      ? 'Delivery days, lead times and cutoff times are read in this time zone.'
      : 'Not set. Until you pick one, delivery days and cutoff times are read in US Pacific time.';

  return (
    <Autocomplete
      label="Time zone"
      placeholder="Search for a city or region"
      aria-label="Distributor time zone"
      data-testid="supplier-timezone-field"
      isRequired={isCreate}
      items={visibleOptions}
      selectedKey={timezone || null}
      inputValue={query}
      onInputChange={setQuery}
      errorMessage={errors.timezone?.message}
      isInvalid={!!errors.timezone}
      description={description}
      onSelectionChange={(key) => {
        // Clearing the input fires with null; keep the saved value rather than
        // writing an empty string, which would wipe the setting.
        if (key === null) return;
        const picked = String(key);
        setValue('timezone', picked, {
          shouldDirty: true,
          shouldValidate: true,
        });
        // The effect above only fires when the label actually changes, so re-picking
        // the zone already stored would otherwise leave the half-typed query.
        setQuery(
          options.find((option) => option.value === picked)?.label ?? picked
        );
      }}
    >
      {visibleOptions.map((option) => (
        <AutocompleteItem key={option.value} textValue={option.label}>
          {option.label}
        </AutocompleteItem>
      ))}
    </Autocomplete>
  );
};
